import { useState } from 'react';
import { Table } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { formatCurrency } from 'shared/utils/common.utils';
import { DatePicker, Input, Select, Tooltip } from 'antd';
import { format } from 'date-fns';
import NoData from 'shared/design-system/components/no-data-box';
import ModalAction from 'shared/design-system/components/modal-action';
import useGetBillingData from './_hooks/useGetBillingData';
import useDownloadBillIcs from './_hooks/useDownloadBillIcs';
import FormBootstrap from 'react-bootstrap/Form';
import dayjs from 'dayjs';
import useGetBillingOptions from './_hooks/useGetBillingOptions';
import useEditBill, { EditableStatoAvanzamentoLavori } from './_hooks/useEditBill';
import useSaveBill from './_hooks/useSaveBill';
import useGetBillingTotals from './_hooks/useGetBillingTotals';
import BillingTotalsTable from './_components/billing-totals-table';
import { useAppDispatch } from 'core/store/hooks';
import { deleteStatoAvanzamentoBilling, getBillingPlan } from './_redux/actions';
import './styles.scss';

interface Props {
  projectName: string | undefined;
  projectId: number;
  setIsBillingDirty: React.Dispatch<React.SetStateAction<boolean>>;
  readOnly?: boolean;
}

const BillingPlan: React.FC<Props> = ({ projectName, projectId, setIsBillingDirty, readOnly = false }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { billingPlan, setBillingPlan, isEditEnabled } = useGetBillingData(projectId);
  const { totalAmount, totalSalAmount } = useGetBillingTotals(billingPlan);
  // Riga SAL (già salvata sul BE) in attesa di conferma di cancellazione tramite `ModalAction`.
  const [salIdPendingDeletion, setSalIdPendingDeletion] = useState<number | undefined>(undefined);
  const [showDeleteSalModal, setShowDeleteSalModal] = useState<boolean>(false);

  // Disabilita completamente quando readOnly è true
  const effectiveIsEditEnabled = readOnly ? false : isEditEnabled;
  const { mappedBillingOptions } = useGetBillingOptions();
  const { downloadIcs } = useDownloadBillIcs(projectName);
  const {
    handleChangeCheckbox,
    handleChangeDate,
    handleSelectChange,
    handleChangeImporto,
    handleAddChild,
    handleRemoveChild,
  } = useEditBill(setBillingPlan, setIsBillingDirty);
  const { errors, handleSaveWithValidation } = useSaveBill(projectId, setIsBillingDirty, setBillingPlan);

  return (
    <>
      {billingPlan?.length ? (
        <div className="d-flex flex-column" style={{ height: 'calc(100vh - 315px)' }}>
          <div className="overflow-auto flex-grow-1">
          <Table bordered>
            <thead>
              <tr>
                <th>{t('project-detail.billing-plan.table.name')}</th>
                <th>{t('project-detail.billing-plan.table.stage')}</th>
                <th>{t('project-detail.billing-plan.table.order')}</th>
                <th>{t('project-detail.billing-plan.table.date')}</th>
                <th className="text-end">{t('project-detail.billing-plan.table.amount')}</th>
                <th className="text-center">{t('project-detail.billing-plan.table.sal')}</th>
                <th>{t('project-detail.billing-plan.table.sending-date')}</th>
                <th>{t('project-detail.billing-plan.table.block')}</th>
                <th>{t('project-detail.billing-plan.table.further-amount')}</th>
                <th>{t('project-detail.billing-plan.table.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {billingPlan?.map((billing) =>
                // Un `<tr>` per ogni elemento di `statoAvanzamentoLavoriList` (il primo, per
                // `creationDate`, è il "padre"; gli altri sono le righe "figlie" aggiunte
                // client-side). Le colonne condivise della milestone (name/stage/order/amount/
                // date) restano identiche su tutte le righe dello stesso gruppo, ripetute e non
                // "rowspan-ate" per requisito di spec. Se la lista fosse vuota/undefined non
                // viene renderizzata alcuna riga per quella milestone, senza far crashare la UI.
                (billing.statoAvanzamentoLavoriList ?? []).map((salItem, index) => {
                  const sal = salItem as EditableStatoAvanzamentoLavori;
                  // Chiave stabile della riga SAL: `id` reale per righe già salvate, `tempId`
                  // client-side per righe bozza non ancora salvate (vedi useEditBill.ts).
                  const salRowKey = (sal.id ?? sal.tempId) as number | string;
                  const isSavedSalRow = sal.id !== undefined;
                  // Il primo elemento (ordinato per creationDate in useGetBillingData.ts) è
                  // sempre il "padre": solo lui mostra i valori condivisi della milestone
                  // (name/stage/order/date/amount, identici per definizione su tutte le righe
                  // del gruppo) e il bottone "aggiungi figlio" — un figlio non può a sua volta
                  // generare un altro figlio.
                  const isParentRow = index === 0;

                  return (
                    <tr key={`${billing.id}-${salRowKey}`} className={!isParentRow ? 'sal-child-row' : undefined}>
                      <td>
                        {isParentRow && (
                          <a
                            href={`https://links.lightning.force.com/lightning/r/InvoiceMilestone__c/${billing.id}/view`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: '#22519e' }}
                          >
                            {billing.name}
                          </a>
                        )}
                      </td>
                      <td>{isParentRow ? billing.stage : ''}</td>
                      <td>{isParentRow ? billing.order : ''}</td>
                      <td>{isParentRow ? (billing.date ? format(billing.date, 'dd/MM/yy') : '-') : ''}</td>
                      <td className="text-end">{isParentRow ? `${formatCurrency(billing.amount)} €` : ''}</td>
                      <td className="text-center">
                        <FormBootstrap.Check
                          type={'checkbox'}
                          onChange={(e) => handleChangeCheckbox(e, billing.id as string, salRowKey)}
                          checked={sal.isInviato}
                          disabled={!effectiveIsEditEnabled}
                        />
                      </td>
                      <td>
                        <DatePicker
                          size="middle"
                          className="w-100"
                          format="DD/MM/YYYY"
                          placeholder={t('project-detail.billing-plan.placeholders.select-date')}
                          value={sal.dataDiInvio ? dayjs(sal.dataDiInvio) : undefined}
                          onChange={(e) => handleChangeDate(e, billing.id as string, salRowKey)}
                          disabled={!effectiveIsEditEnabled}
                          status={errors[salRowKey]?.date && !sal.dataDiInvio ? 'error' : undefined}
                        />
                      </td>
                      <td style={{ width: '200px' }}>
                        <Select
                          className="w-100"
                          allowClear
                          placeholder={t('project-detail.billing-plan.placeholders.select-block')}
                          value={sal.idTipologiaBlocco?.value}
                          onChange={(e) => handleSelectChange(e, billing.id as string, salRowKey)}
                          disabled={!effectiveIsEditEnabled}
                          defaultValue={
                            mappedBillingOptions?.mappedTipologiaBloccoEnumList?.find(
                              (el) => el.value === 'LAVORAZIONI_IN_CORSO'
                            )?.value
                          }
                          status={errors[salRowKey]?.block ? 'error' : undefined}
                        >
                          {mappedBillingOptions?.mappedTipologiaBloccoEnumList?.map((item) => (
                            <Select.Option key={item.value} value={item.value}>
                              {item.label}
                            </Select.Option>
                          ))}
                        </Select>
                      </td>
                      <td style={{ width: '150px' }}>
                        <Input
                          type="number"
                          allowClear
                          placeholder={t('project-detail.billing-plan.placeholders.insert-amount')}
                          value={sal.importo}
                          onChange={(e) => handleChangeImporto(e.target.value, billing.id as string, salRowKey)}
                          disabled={!effectiveIsEditEnabled}
                          status={errors[salRowKey]?.amount && !sal.importo ? 'error' : undefined}
                        />
                      </td>
                      <td style={{ width: '90px' }}>
                        <div className="d-flex">
                          <Tooltip
                            placement="top"
                            color="white"
                            title={t('project-detail.billing-plan.actions.download')}
                          >
                            <button
                              onClick={() => {
                                downloadIcs(billing);
                              }}
                              className="btn-icon"
                            >
                              <i className={'icon-file-download text-blue-links me-1'} />
                            </button>
                          </Tooltip>
                          <Tooltip placement="top" color="white" title={t('project-detail.billing-plan.actions.save')}>
                            <button
                              onClick={() => {
                                handleSaveWithValidation(billing, sal);
                              }}
                              className="btn-icon"
                              disabled={!effectiveIsEditEnabled}
                            >
                              <i
                                className={`icon-save ${
                                  effectiveIsEditEnabled ? 'text-blue-links' : 'text-placeholder'
                                } me-1`}
                              />
                            </button>
                          </Tooltip>
                          {isParentRow && (
                            <Tooltip
                              placement="top"
                              color="white"
                              title={t('project-detail.billing-plan.actions.add-child')}
                            >
                              <button
                                onClick={() => {
                                  handleAddChild(billing.id as string, salRowKey);
                                }}
                                className="btn-icon"
                                disabled={!effectiveIsEditEnabled || !isSavedSalRow}
                              >
                                <i
                                  className={`icon-calendar-add ${
                                    effectiveIsEditEnabled && isSavedSalRow ? 'text-blue-links' : 'text-placeholder'
                                  } me-1`}
                                />
                              </button>
                            </Tooltip>
                          )}
                          {/* Visibile su ogni riga FIGLIA (mai sul padre, `isParentRow` sempre
                              false qui), sia bozza che già salvata sul BE. Righe bozza:
                              comportamento invariato (rimozione locale immediata via
                              `handleRemoveChild`, nessuna modale/API). Righe già salvate
                              (`isSavedSalRow`): richiede conferma tramite `ModalAction` prima
                              di dispatchare la DELETE. */}
                          {!isParentRow && (
                            <Tooltip
                              placement="top"
                              color="white"
                              title={t('project-detail.billing-plan.actions.delete')}
                            >
                              <button
                                onClick={() => {
                                  if (isSavedSalRow) {
                                    setSalIdPendingDeletion(sal.id as number);
                                    setShowDeleteSalModal(true);
                                  } else {
                                    handleRemoveChild(billing.id as string, sal.tempId as string);
                                  }
                                }}
                                className="btn-icon"
                                disabled={!effectiveIsEditEnabled}
                              >
                                <i className={'icon-delete text-red me-1'} />
                              </button>
                            </Tooltip>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </Table>
          </div>
          <BillingTotalsTable totalAmount={totalAmount} totalSalAmount={totalSalAmount} />
        </div>
      ) : (
        <div className="d-flex justify-content-center align-items-center">
          <NoData msg={t('project-detail.billing-plan.no-invoices')} />
        </div>
      )}
      <ModalAction
        title={t('project-detail.billing-plan.actions.delete')}
        actionLabel={t('common.delete')}
        cancelLabel={t('common.cancel')}
        action={() => {
          if (salIdPendingDeletion === undefined) return;
          // `deleteStatoAvanzamentoBilling` gestisce internamente gli errori con `errorHandler`
          // (toast globale) senza rilanciare l'eccezione: la promise si risolve sempre, sia in
          // caso di successo che di errore già gestito. Il refetch successivo (stesso pattern di
          // `useSaveBill.ts`) è quindi sicuro in entrambi i casi: se la DELETE è andata a buon
          // fine la riga non torna più dal BE, se è fallita il refetch semplicemente ripropone lo
          // stato invariato (nessuna rimozione locale ad hoc necessaria).
          dispatch(deleteStatoAvanzamentoBilling({ id: salIdPendingDeletion })).then(() => {
            dispatch(getBillingPlan(projectId));
          });
          setSalIdPendingDeletion(undefined);
        }}
        setShow={setShowDeleteSalModal}
        show={showDeleteSalModal}
      >
        <span className="text-danger">
          <i className="fa-solid fa-triangle-exclamation" /> <b>{t('common.attention')}</b>{' '}
        </span>
        <div className="p-1">
          <p>{t('project-detail.billing-plan.delete-confirmation')}</p>
        </div>
      </ModalAction>
    </>
  );
};

export default BillingPlan;
